import type {
  City,
  Country,
  State,
} from "react-country-state-city/dist/esm/types";

import { Info } from "lucide-react";

import { Label, TextField } from "@/components/field";
import {
  Select,
  SelectButton,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import {
  NonFousableTooltipTarget,
  Tooltip,
  TooltipTrigger,
} from "@/components/tooltip";

// eslint-disable-next-line react-refresh/only-export-components
export { useLocationData } from "./useLocationData";

interface CountrySelectProps {
  className?: string;
  onChange: (id: number, name: string) => void;
  options: Country[];
  value?: string;
}

export function CountrySelect({
  className,
  onChange,
  options,
  value,
}: CountrySelectProps) {
  return (
    <TextField className={className}>
      <Label>Country</Label>
      <Select
        onSelectionChange={(id) => {
          const country = options.find((o) => o.id === Number(id));

          if (country) onChange(country.id, country.name);
        }}
        selectedKey={options.find((c) => c.name === value)?.id.toString()}
      >
        <SelectButton />
        <SelectPopover>
          <SelectListBox className="max-h-64">
            {options.map((country) => (
              <SelectListItem
                id={country.id.toString()}
                key={country.id}
                textValue={country.name}
              >
                {country.name}
              </SelectListItem>
            ))}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </TextField>
  );
}

interface StateSelectProps {
  className?: string;
  disabled: boolean;
  onChange: (id: number, name: string) => void;
  options: State[];
  value?: string;
}

export function StateSelect({
  className,
  disabled,
  onChange,
  options,
  value,
}: StateSelectProps) {
  return (
    <TextField className={className}>
      <div className="flex items-center gap-1">
        <Label>State</Label>
        <TooltipTrigger delay={250}>
          <NonFousableTooltipTarget>
            <Info className="size-3 mb-1 text-text-tertiary hover:text-text-primary" />
          </NonFousableTooltipTarget>
          <Tooltip>Or provinces, if applicable</Tooltip>
        </TooltipTrigger>
      </div>
      <Select
        isDisabled={disabled}
        onSelectionChange={(id) => {
          const state = options.find((o) => o.id === Number(id));

          if (state) {
            onChange(state.id, state.name);
          }
        }}
        selectedKey={options.find((s) => s.name === value)?.id.toString()}
      >
        <SelectButton />
        <SelectPopover>
          <SelectListBox className="max-h-64">
            {options.map((state) => (
              <SelectListItem
                id={state.id.toString()}
                key={state.id}
                textValue={state.name}
              >
                {state.name}
              </SelectListItem>
            ))}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </TextField>
  );
}

interface CitySelectProps {
  className?: string;
  disabled: boolean;
  onChange: (id: number, name: string) => void;
  options: City[];
  value?: string;
}

export function CitySelect({
  className,
  disabled,
  onChange,
  options,
  value,
}: CitySelectProps) {
  return (
    <TextField className={className}>
      <Label>City</Label>
      <Select
        isDisabled={disabled}
        onSelectionChange={(id) => {
          const city = options.find((o) => o.id === Number(id));

          if (city) {
            onChange(city.id, city.name);
          }
        }}
        selectedKey={options.find((c) => c.name === value)?.id.toString()}
      >
        <SelectButton />
        <SelectPopover>
          <SelectListBox className="max-h-64">
            {options.map((city) => (
              <SelectListItem
                id={city.id.toString()}
                key={city.id}
                textValue={city.name}
              >
                {city.name}
              </SelectListItem>
            ))}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </TextField>
  );
}
